import Bowser from 'bowser';
import { ClientJS } from 'clientjs';
import { getCurrency } from 'locale-currency';
import { IWebDevice } from '@/interfaces/IWebDevice';
import { requestNotificationPermission } from '../api/services/PushNotificationService';
import { ref, computed } from 'vue';

declare global {
	interface Window {
		chrome?: any;
	}
}

const browser = Bowser.getParser(window.navigator.userAgent);

// Create singleton state
const deviceParams = ref<IWebDevice | null>(null);
const isLoading = ref(true);
const error = ref<Error | null>(null);
let isInitialized = false;

function isPushNotificationSupported(): boolean {
	return 'Notification' in window && 
		   'serviceWorker' in navigator && 
		   'PushManager' in window;
}

async function initializeDeviceParams(): Promise<IWebDevice> {
	const client = new ClientJS();
	let token = null;
	
	if (isPushNotificationSupported()) {
		token = await requestNotificationPermission();
	}

	return {
		user_agent: client.getUserAgent(),
		browser_name: client.getBrowser(),
		browser_version: client.getBrowserVersion(),
		os_name: client.getOS(),
		os_version: client.getOSVersion(),
		locale: navigator.language,
		locale_language: navigator.language.slice(0, 2),
		locale_country: navigator.language.slice(3, 5),
		locale_currency: getCurrency(navigator.language),
		timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		is_touch_enabled: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
		device_type: browser.getPlatformType(),
		browser_fingerprint_id: client.getFingerprint().toString(),
		web_push_subscription: token ?? undefined,
	};
}

// Initialize function that ensures we only load once
async function ensureInitialized() {
	if (!isInitialized) {
		isLoading.value = true;
		isInitialized = true;
		try {
			deviceParams.value = await initializeDeviceParams();
		} catch (e) {
			isInitialized = false;
			error.value = e as Error;
			console.error('Failed to load device params:', e);
		} finally {
			isLoading.value = false;
		}
	}
	return deviceParams.value;
}

// Composable that provides access to the singleton state
export function useDeviceParams() {
	// Ensure initialization happens only once
	if (!isInitialized) {
		ensureInitialized();
	}

	return {
		deviceParams: computed(() => deviceParams.value),
		isLoading: computed(() => isLoading.value),
		error: computed(() => error.value),
		pushNotificationsSupported: computed(() => isPushNotificationSupported()),
		reloadDeviceParams: async () => {
			isInitialized = false;
			return ensureInitialized();
		}
	};
}